import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  MarkingCodeItem,
  MarkingCodeFilters,
  SortField,
  FilterStatusOption,
  FilterTypeOption
} from '../types';
import {
  API_BASE,
  APP_VERSION,
  DEFAULT_KM_TYPES,
  DEFAULT_KM_STATUSES
} from '../constants';

const INITIAL_FILTERS: MarkingCodeFilters = {
  search: '',
  type: 'all',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  selectedStorage: null,
  selectedPharmacy: null,
  selectedProduct: null,
  orderBy: 'date',
  order: 'desc'
};

export const useAdminMarkingCodes = () => {
  const [items, setItems] = useState<MarkingCodeItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<MarkingCodeFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  // Справочники фильтров, загружаемые с бэкенда
  const [pharmacies, setPharmacies] = useState<string[]>([]);
  const [storages, setStorages] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<FilterStatusOption[]>(DEFAULT_KM_STATUSES);
  const [types, setTypes] = useState<FilterTypeOption[]>(DEFAULT_KM_TYPES);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Загрузка справочников с бэкенда (/api/v1/marking-codes/filter-options)
  const fetchFilterOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/marking-codes/filter-options`, {
        params: { version: APP_VERSION }
      });

      if (response.data?.success && response.data?.data) {
        const {
          pharmacies: fetchedPharmacies,
          statuses: fetchedStatuses,
          types: fetchedTypes,
          storages: fetchedStorages
        } = response.data.data;

        if (Array.isArray(fetchedPharmacies) && fetchedPharmacies.length > 0) {
          setPharmacies(fetchedPharmacies);
        }

        if (Array.isArray(fetchedStorages) && fetchedStorages.length > 0) {
          setStorages(fetchedStorages);
        }

        if (Array.isArray(fetchedStatuses) && fetchedStatuses.length > 0) {
          setStatuses([{ zid: 'all', nameRu: 'Все' }, ...fetchedStatuses]);
        }

        if (Array.isArray(fetchedTypes) && fetchedTypes.length > 0) {
          setTypes([{ zid: 'all', nameRu: 'Все' }, ...fetchedTypes]);
        }
      }
    } catch (e) {
      console.warn('Справочники фильтров загружены по умолчанию или недоступны:', e);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Загрузка кодов маркировки с бэкенда (/api/v1/admin/marking-codes)
  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    try {
      const params: Record<string, any> = {
        version: APP_VERSION,
        page: page + 1,
        limit: pageSize,
        orderBy: filters.orderBy,
        order: filters.order
      };

      if (filters.search) params.search = filters.search.trim();
      if (filters.type && filters.type !== 'all') params.type = filters.type;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.selectedPharmacy) params.pharmacy = filters.selectedPharmacy;
      if (filters.selectedStorage) params.storage = filters.selectedStorage;
      if (filters.selectedProduct) params.product = filters.selectedProduct;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await axios.get(`${API_BASE}/admin/marking-codes`, {
        params,
        signal: abortControllerRef.current.signal
      });

      if (response.data?.success && response.data?.data) {
        setItems(response.data.data.items || []);
        setTotalCount(response.data.data.total || 0);
      }
    } catch (error: any) {
      if (axios.isCancel(error)) return;
      console.error('Ошибка загрузки кодов маркировки (admin):', error);
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Не удалось загрузить данные кодов маркировки',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, filters]);

  // Дебаунс при изменении фильтров и загрузка
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 250);

    return () => clearTimeout(timer);
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    fetchFilterOptions();
    fetchData();
    setNotification({
      open: true,
      message: 'Данные обновлены',
      severity: 'info'
    });
  }, [fetchFilterOptions, fetchData]);

  // Выгрузка в Excel
  const handleExport = useCallback(() => {
    const params = new URLSearchParams({
      version: APP_VERSION || '1.0.9',
      orderBy: String(filters.orderBy),
      order: String(filters.order)
    });

    if (filters.search) params.append('search', filters.search.trim());
    if (filters.type && filters.type !== 'all') params.append('type', String(filters.type));
    if (filters.status && filters.status !== 'all') params.append('status', String(filters.status));
    if (filters.selectedPharmacy) params.append('pharmacy', filters.selectedPharmacy);
    if (filters.selectedStorage) params.append('storage', filters.selectedStorage);
    if (filters.selectedProduct) params.append('product', filters.selectedProduct);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);

    window.open(`${API_BASE}/admin/marking-codes/export?${params.toString()}`, '_blank');
  }, [filters]);

  const handleFilterChange = useCallback((newFilters: Partial<MarkingCodeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      orderBy: field,
      order: prev.orderBy === field ? (prev.order === 'asc' ? 'desc' : 'asc') : 'desc'
    }));
    setPage(0);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(0);
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    items,
    totalCount,
    isLoading,
    filters,
    page,
    pageSize,
    pharmacies,
    storages,
    statuses,
    types,
    optionsLoading,
    notification,
    setPage,
    setPageSize,
    handleRefresh,
    handleExport,
    handleFilterChange,
    handleSort,
    handleResetFilters,
    closeNotification
  };
};
